import styled, { keyframes } from 'styled-components'

export const RunTabsWrapper = styled.div`
  margin-bottom: 0.75rem;
`

export const RunTabsRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`

export const RunTabButton = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.4375rem;
  height: 2.125rem;
  padding: 0 0.875rem;
  border-radius: 0.5rem;
  border: 0.0625rem solid
    ${({ theme, $active }) => ($active ? theme.primaryColor : theme.grey300Color)};
  background: ${({ theme, $active }) => ($active ? theme.primary100Color : theme.whiteColor)};
  color: ${({ theme, $active }) => ($active ? theme.primaryColor : theme.grey900Color)};
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.875rem;
  font-weight: ${({ $active }) => ($active ? 500 : 400)};
  cursor: pointer;
  white-space: nowrap;

  &:focus-visible {
    outline: 0.125rem solid ${({ theme }) => theme.primaryColor};
    outline-offset: 0.125rem;
  }
`

export const RunTabDot = styled.span<{ $color: string }>`
  width: 0.5rem;
  height: 0.5rem;
  border-radius: 50%;
  flex: none;
  background: ${({ $color }) => $color};
`

export const NewRunTabButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  height: 2.125rem;
  padding: 0 0.875rem;
  border-radius: 0.5rem;
  border: 0.0625rem ${({ $active }) => ($active ? 'solid' : 'dashed')}
    ${({ theme, $active }) => ($active ? theme.primaryColor : theme.grey400Color)};
  background: ${({ theme, $active }) => ($active ? theme.primary100Color : 'transparent')};
  color: ${({ theme, $active }) => ($active ? theme.primaryColor : theme.grey900Color)};
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.875rem;
  font-weight: ${({ $active }) => ($active ? 500 : 400)};
  white-space: nowrap;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`

// BRAND-9511: "N of M runs used" indicator at the right end of the tab
// strip — only rendered when the BE sends max_sourcing_runs. The exhausted
// (at-limit) treatment darkens/emphasises it so the terminal state reads at
// a glance next to the disabled "+ New run".
export const RunTabsUsage = styled.span<{ $exhausted?: boolean }>`
  margin-left: auto;
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.8125rem;
  font-weight: ${({ $exhausted }) => ($exhausted ? 500 : 400)};
  color: ${({ theme, $exhausted }) => ($exhausted ? '#fc5151' : theme.grey700Color)};
  white-space: nowrap;
`

export const RunRecordBar = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  padding: 0.75rem 1.25rem;
  margin-bottom: 0.75rem;
  background: ${({ theme }) => theme.whiteColor};
  border-radius: 0.5rem;
  box-shadow: 0 0.125rem 0.375rem rgb(0 0 0 / 4%);
`

export const RunRecordName = styled.span`
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ theme }) => theme.grey900Color};
  white-space: nowrap;
`

export const RunRecordStatusPill = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  height: 1.25rem;
  padding: 0.125rem 0.5rem;
  border-radius: 0.625rem;
  background: ${({ $color }) => $color};
  color: ${({ theme }) => theme.whiteColor};
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.6875rem;
  font-weight: 500;
  letter-spacing: 0.0625rem;
  text-transform: uppercase;
  white-space: nowrap;
`

export const RunRecordProgress = styled.span`
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.grey800Color};
  white-space: nowrap;
`

export const RunRecordChannelCounts = styled.span`
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.grey800Color};
  white-space: nowrap;
`

export const RunRecordDate = styled.span`
  margin-left: auto;
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.75rem;
  color: ${({ theme }) => theme.grey700Color};
  white-space: nowrap;
`

// RUNNING/FAILED treatment (BRAND-9307): these render on their own full-width
// line below the record bar's top info row — RunRecordBar is a wrapping flex
// container, so a 100%-basis child forces the wrap rather than needing a
// separate layout element.
const progressSweep = keyframes`
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(250%);
  }
`

export const RunRecordProgressTrack = styled.div`
  flex: 1 1 100%;
  width: 100%;
  height: 0.25rem;
  margin-top: 0.25rem;
  border-radius: 0.125rem;
  background: ${({ theme }) => theme.grey200Color};
  overflow: hidden;
`

export const RunRecordProgressFill = styled.div`
  width: 40%;
  height: 100%;
  border-radius: 0.125rem;
  background: #00b9d5;
  animation: ${progressSweep} 1.4s ease-in-out infinite;
`

export const RunRecordNote = styled.p`
  flex: 1 1 100%;
  width: 100%;
  margin: 0.375rem 0 0;
  font-family: Rubik, verdana, sans-serif;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.grey700Color};
`

export const RunRecordFailedNote = styled(RunRecordNote)`
  color: #fc5151;
`
